<script lang="ts">
import { Form } from 'vee-validate';
import { defineComponent, PropType, ref } from 'vue';
import { useStore } from 'vuex';

import { createContact } from '@/api/contacts';
import PlusIcon from '@/components/base/assets/PlusIcon.vue';
import TextButton from '@/components/base/TextButton.vue';
import ThemedButton from '@/components/base/ThemedButton.vue';
import AddressForm from '@/components/checkout/AddressForm.vue';
import AddressList from '@/components/checkout/steps/address/AddressList.vue';
import GiftRecipientForm from '@/components/checkout/steps/address/GiftRecipientForm.vue';
import { useCallback } from '@/composables/useCallback';
import { useCheckout } from '@/composables/useCheckout';
import { useAddressFields, useForm } from '@/composables/useForm';
import { useCustomer } from '@/stores/customer';
import { NutsAddress } from '@/utils/address';

type FormState = 'add' | 'edit' | 'list';

export default defineComponent({
  name: 'ChangeAddress',
  props: {
    addresses: { required: true, type: Array as PropType<NutsAddress[]> },
    containsGiftCertificate: { required: false, type: Boolean, default: false },
    isButtonLoading: { required: false, type: Boolean, default: false },
    shippingAddress: { required: false, type: Object as PropType<NutsAddress | null> },
    showMakeDefault: { required: false, type: Boolean },
  },
  components: {
    AddressForm,
    AddressList,
    // eslint-disable-next-line vue/no-reserved-component-names
    Form,
    GiftRecipientForm,
    PlusIcon,
    TextButton,
    ThemedButton,
  },
  emits: [
    'cancelled-address-suggestions',
    'change-address',
    'save-address',
    'selected-address-suggestion',
    'tooltip',
  ],
  setup(props, { emit }) {
    const formState = ref<FormState>('list');
    const store = useStore();
    const { initializeAddress } = useAddressFields();
    const { cxMode, isB2B } = useCheckout(store);
    const customerStore = useCustomer();
    const { validatorFailed } = useForm();

    const addNewAddress = () => {
      formState.value = 'add';
    };

    const addressToEdit = ref<NutsAddress>(initializeAddress());
    const giftRecipientEmail = ref(
      (props.containsGiftCertificate && props.shippingAddress?.email) || '',
    );
    const giftRecipientName = ref(
      (props.containsGiftCertificate && props.shippingAddress?.name) || '',
    );

    const submitAddress = async (address: NutsAddress) => {
      let updatedAddress = address;
      if (address.id) {
        const { email, ...addressWithoutGiftingEmail } = address;
        updatedAddress = await customerStore.updateAddress(
          addressWithoutGiftingEmail,
          cxMode.value ? customerStore.customer?.email : undefined,
        );
      }
      const event = updatedAddress?.id ? 'change-address' : 'save-address';
      emit(event, updatedAddress);
    };

    const submitAddressWithGiftCertificate = async (address: NutsAddress) => {
      let addressToSubmit = address;

      if (props.shippingAddress?.email) {
        addressToSubmit = {
          ...addressToSubmit,
          email: props.shippingAddress?.email,
        };

        await submitAddress(addressToSubmit);
      } else {
        if (address.id) {
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
          const { email, ...addressWithoutGiftingEmail } = address;
          await customerStore.updateAddress(addressWithoutGiftingEmail);
        }
        formState.value = 'list';
      }
    };

    const onAddressSubmitted = async (address: NutsAddress) => {
      if (customerStore.customer) {
        const b2bSource = cxMode.value
          ? 'cx checkout buying for business'
          : 'checkout buying for business';
        createContact({
          email: customerStore.customer.email,
          b2b: isB2B.value,
          b2bSource: isB2B.value ? b2bSource : undefined,
          subscribed: 'unchecked',
        });
      }
      if (props.containsGiftCertificate) {
        await submitAddressWithGiftCertificate(address);
      } else {
        await submitAddress(address);
      }
    };

    const onEditAddress = (addressId: string) => {
      const address = props.addresses.find((a) => a.id === addressId);
      addressToEdit.value = initializeAddress(address);
      formState.value = 'edit';
    };

    const onSetAsDefaultAddress = useCallback(async (addressId: string) => {
      const address = props.addresses.find((a) => a.id === addressId);
      if (address) {
        const email = cxMode.value ? customerStore.customer?.email : undefined;
        await customerStore.updateAddress({ ...address, default: true }, email);
      }
    });

    const selectedAddressId = ref(props.shippingAddress?.id ?? props.addresses[0]?.id);

    const useSelectedAddress = () => {
      let address;
      if (selectedAddressId.value !== props.shippingAddress?.id || giftRecipientEmail.value) {
        address = props.addresses.find((a) => a.id === selectedAddressId.value) ?? {};
        if (giftRecipientEmail.value) {
          address = {
            ...address,
            email: giftRecipientEmail.value,
          };
        }
      }

      emit('change-address', address);
    };

    const viewSavedAddresses = () => {
      formState.value = 'list';
      addressToEdit.value = initializeAddress();
    };

    const onTooltip = (location: string) => {
      emit('tooltip', location);
    };

    const onSelectedAddressSuggestion = () => emit('selected-address-suggestion');
    const onCancelledAddressSuggestions = () => emit('cancelled-address-suggestions');

    return {
      addNewAddress,
      addressToEdit,
      formState,
      giftRecipientEmail,
      giftRecipientName,
      onCancelledAddressSuggestions,
      onSelectedAddressSuggestion,
      onAddressSubmitted,
      onEditAddress,
      onSetAsDefaultAddress,
      onTooltip,
      plusIcon: nutshell['img/plus.svg'],
      selectedAddressId,
      useSelectedAddress,
      validatorFailed,
      viewSavedAddresses,
    };
  },
});
</script>

<template>
  <transition name="fade">
    <!-- eslint-disable vue/no-mutating-props -->
    <div v-if="formState === 'list'" key="list">
      <Form v-slot="{ meta }" data-test="change-address" @submit="useSelectedAddress">
        <AddressList
          v-model="selectedAddressId"
          :addresses
          :isPending="onSetAsDefaultAddress.isPending"
          showSetAsDefaultButton
          @edit-address="onEditAddress"
          @set-as-default="onSetAsDefaultAddress.execute"
        />
        <TextButton
          class="flex items-center mt-4 md:mt-6"
          @click="addNewAddress"
          @keydown.enter.prevent="addNewAddress"
          @keydown.space.prevent="addNewAddress"
          data-test="change-address-add-new"
        >
          <PlusIcon :size="16" />
          Add New Address
        </TextButton>
        <GiftRecipientForm
          v-if="containsGiftCertificate"
          v-model:email="giftRecipientEmail"
          v-model:name="giftRecipientName"
          class="mt-6 md:mt-8"
          :showName="false"
        />
        <ThemedButton
          type="submit"
          class="mt-4 md:mt-8"
          data-test="change-address-continue"
          :disabled="validatorFailed(meta)"
          :isLoading="isButtonLoading"
          theme="gray"
        >
          {{ containsGiftCertificate ? 'Continue' : 'Continue with this Address' }}
        </ThemedButton>
      </Form>
    </div>
    <div v-else key="form">
      <strong class="text-base leading-4">
        <template v-if="formState === 'add'">Add a New Address</template>
        <template v-if="formState === 'edit'">Edit Shipping Address</template>
      </strong>
      <AddressForm
        v-model="addressToEdit"
        class="mt-5"
        uniqueIdSuffix="change-address"
        :showMakeDefault
        @address-submitted="onAddressSubmitted"
        @tooltip="onTooltip"
        @selected-address-suggestion="onSelectedAddressSuggestion"
        @cancelled-address-suggestions="onCancelledAddressSuggestions"
      >
        <template v-slot:formControls="{ meta }">
          <div class="flex items-center mt-5 md:mt-8">
            <ThemedButton
              type="submit"
              theme="gray"
              :disabled="validatorFailed(meta)"
              :isLoading="isButtonLoading"
              data-test="address-multi-ship-submit"
            >
              Save &amp; Continue
            </ThemedButton>
            <TextButton
              class="ml-7"
              @click="viewSavedAddresses"
              @keydown.enter.prevent="viewSavedAddresses"
              @keydown.space.prevent="viewSavedAddresses"
            >
              Cancel
            </TextButton>
          </div>
        </template>
      </AddressForm>
    </div>
  </transition>
</template>
